import React, { useState, useEffect, useRef } from "react";
import styles from "../CreateLeadAutomationForm.module.css";
import deleteImage from "../../../Customize/Tabs/CustomizeTab/CustomizedField/Images/delete.svg";
import DeleteBtn from "../../../../../../../components/Buttons/DeleteBtn";
import DeletePopup from "../../../../../../../components/CommonPopups/DeletePopup";
import selectedValueAutomation from "../../../../../../../assets/images/selectedValueAutomation.svg";

function FinalCustomizedDropdown(props: any) {
  const {
    field,
    fields = [], // 👈 full fields for reading Assignee Method
    onFieldChange,
    deleteAction = false,
    onDelete,
    edit = false,
  } = props;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(field?.options || []);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);

  // 🔥 Detect Assignee Method from the form
  const assigneeMethod = fields?.find(
    (f: any) => f.fieldName === "Assignee Method"
  )?.selectedOption;

  // 🔥 Determine if this field is Assignee & must allow multi-select
  const isAssigneeField = field?.fieldName === "Assignee";
  const isMultiSelect =
    isAssigneeField && assigneeMethod === "Multiple Assignee";

  const getSelectedArray = () => {
    const sel = field?.selectedOption;
    if (Array.isArray(sel)) return sel;
    if (sel && typeof sel === "object") return [sel];
    return [];
  };

  // 🔍 Filtering options
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions(field?.options || []);
    } else {
      const filtered = (field?.options || []).filter((opt: any) =>
        opt.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, field?.options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const capitalizeSentences = (text: string) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const handleDeleteConfirmed = () => {
    setShowDeletePopup(false);
    onDelete(field);
  };

  const handleDeleteCancelled = () => setShowDeletePopup(false);

  // 🔥 Main Select Handler
  // const handleOptionSelect = (opt: any) => {
  //   if (isMultiSelect) {
  //     const selectedArr = getSelectedArray();
  //     const exists = selectedArr.some((o: any) => o?.id === opt?.id);

  //     const updated = exists
  //       ? selectedArr.filter((o: any) => o?.id !== opt?.id)
  //       : [...selectedArr, opt];

  //     onFieldChange({
  //       ...field,
  //       selectedOption: updated,
  //     });

  //     return; // keep dropdown open for multi-select
  //   }

  //   // Single select behavior
  //   onFieldChange({
  //     ...field,
  //     selectedOption: opt?.name ?? "",
  //   });

  //   setSearchTerm("");
  //   setIsDropdownOpen(false);
  // };
  const handleOptionSelect = (opt: any) => {
    let updatedValue = opt?.name ?? "";

    // 🎯 MAPPING BASED ON FIELD NAME
    if (field.fieldName === "Source") {
      updatedValue = {
        sourceId: opt?.id,
        sourceName: opt?.name,
        type: opt?.type,
        branchId: opt?.branchId,
      };
    }

    if (field.fieldName === "Course") {
      updatedValue = { courseId: opt.id, courseName: opt.name };
    }

    if (field.fieldName === "Branch") {
      updatedValue = { branchId: opt.id, branchName: opt.name };
    }

    if (field.fieldName === "Batch") {
      updatedValue = { batchId: opt.id, batchName: opt.name };
    }

    // 🔥 ASSIGNEE MULTI SELECT
    if (isMultiSelect) {
      const selectedArr = getSelectedArray();
      const exists = selectedArr.some((o: any) => o?.id === opt?.id);

      const updated = exists
        ? selectedArr.filter((o: any) => o?.id !== opt?.id)
        : [...selectedArr, opt];

      onFieldChange({
        ...field,
        selectedOption: updated,
      });

      return; // keep dropdown open
    }

    // 🔥 SINGLE SELECT
    if (field.fieldName === "Assignee" && !isMultiSelect) {
      onFieldChange({
        ...field,
        selectedOption: { id: opt.id, name: opt.name },
      });
    } else {
      onFieldChange({
        ...field,
        selectedOption: updatedValue,
      });
    }

    setSearchTerm("");
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    if (isDropdownOpen) {
      setSearchTerm("");
    }
  }, [isDropdownOpen]);

  return (
    <div
      ref={dropdownRef}
      className={styles.customizedFieldWrapper}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        position: "relative",
      }}
    >
      {/* LABEL + DELETE */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ fontSize: "14px", fontWeight: 500, color: "#344054" }}>
          {capitalizeSentences(field?.fieldName || "Select Option")}
          {field?.fieldName !== "Batch" && (
            <span style={{ color: "red" }}>*</span>
          )}
        </div>

        {deleteAction && (
          <div className={styles.customizedFieldTopRight}>
            <DeleteBtn
              icon={deleteImage}
              onClick={() => setShowDeletePopup(true)}
            />

            {showDeletePopup && (
              <DeletePopup
                open={showDeletePopup}
                onClose={handleDeleteCancelled}
                noBtnClick={handleDeleteCancelled}
                yesBtnClick={handleDeleteConfirmed}
                title={"Are you sure you want to delete this field?"}
              />
            )}
          </div>
        )}
      </div>

      {/* INPUT / CHIPS AREA */}
      <div
        style={{
          border: "1px solid #E4E7EC",
          borderRadius: 8,
          padding: "6px 10px",
          minHeight: 44,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
          cursor: edit ? "pointer" : "not-allowed",
          background: "#fff",
        }}
        onClick={(e) => {
          if (edit) {
            e.stopPropagation();
            setIsDropdownOpen((prev) => !prev);
          }
        }}
      >
        <img src={selectedValueAutomation} />

        {/* MULTI SELECT UI */}
        {isMultiSelect ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {getSelectedArray().length > 0 ? (
              getSelectedArray().map((opt: any) => (
                <div
                  key={opt.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "4px 8px",
                    background: "#ECFDF3",
                    borderRadius: 12,
                    border: "1px solid #A6F4C5",
                    gap: 6,
                  }}
                >
                  <span style={{ color: "#067647" }}>{opt.name}</span>
                  <span
                    style={{
                      cursor: "pointer",
                      color: "#067647",
                      fontWeight: "bold",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionSelect(opt);
                    }}
                  >
                    ✕
                  </span>
                </div>
              ))
            ) : (
              <span style={{ color: "#98A2B3" }}>Select assignee</span>
            )}
          </div>
        ) : (
          <input
            type="text"
            value={
              isDropdownOpen
                ? searchTerm
                : typeof field?.selectedOption === "object" &&
                  field?.selectedOption !== null
                ? field?.selectedOption?.sourceName ||
                  field?.selectedOption?.courseName ||
                  field?.selectedOption?.branchName ||
                  field?.selectedOption?.batchName ||
                  field?.selectedOption?.name ||
                  ""
                : field?.selectedOption ?? ""
            }
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            onClick={(e) => e.stopPropagation()}
            disabled={!edit}
            style={{
              flexGrow: 1,
              border: "none",
              outline: "none",
              background: "transparent",
            }}
            onFocus={() => setIsDropdownOpen(true)}
          />
        )}

        {/* ARROW */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          style={{
            marginLeft: "auto",
            transition: "0.2s",
            transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <path
            d="M19 9l-7 7-7-7"
            stroke="currentColor"
            strokeWidth={2}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* DROPDOWN LIST */}
      {isDropdownOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            border: "1px solid #E4E7EC",
            borderRadius: 8,
            background: "#fff",
            maxHeight: 200,
            overflowY: "auto",
            zIndex: 20,
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt: any, idx: number) => {
              const isSelected = isMultiSelect
                ? getSelectedArray().some((o: any) => o.id === opt.id)
                : field?.selectedOption === opt.name;

              return (
                <div
                  key={opt.id ?? idx}
                  onClick={() => handleOptionSelect(opt)}
                  style={{
                    padding: "10px 12px",
                    cursor: "pointer",
                    background: isSelected ? "rgba(17,200,155,0.08)" : "#fff",
                    color: isSelected ? "#11C89B" : "#101828",
                    fontWeight: 500,
                  }}
                >
                  {opt.name}
                </div>
              );
            })
          ) : (
            <div style={{ padding: "10px 12px", color: "#98A2B3" }}>
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FinalCustomizedDropdown;
